import React, {
  useMemo,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';

import { ReactComponent as SendIcon } from '../../assets/icons/circle-arrow-icon.svg';

import replaceLastWordWithEmoji from '../../utils/replaceLastWordWithEmoji';

import MessageInputContext from '../../context/MessageInputContext';
import InputExtras from './components/InputExtras';
import AudioMemoControls from './components/AudioMemoControls';
import texts from './texts.json';
import {
  MessageInputWrapper,
  StyledInput,
  SubmitButton,
  ScheduleMessageButton,
  StyledPopoverIcon,
} from './styles';

const MessageInput = ({
  text,
  placeholder,
  onChange,
  onAddAttachments,
  onSubmit,
  onClickSchedule,
  isEditMessage,
  isDisabled,
  isSendingMessage,
  framePadding,
  inputRef,
  isMobileView,
  isScheduleMessage,
  useExtras,
  submitOnEnter,
  hideVoiceRecording,
  hideAttachments,
  showSubmitInEditMode,
  ...extraProps
}) => {
  const {
    isVoiceMemoRecording,
    stopVoiceRecording,
  } = useContext(MessageInputContext);

  const Adornment = useMemo(() => (
    <InputExtras
      onEmojiChange={({ emoji }) => {
        const currentCursorPosition = inputRef.current?.selectionEnd || 0;
        const newValue = `${text.slice(0, currentCursorPosition)}${emoji}${text.slice(currentCursorPosition)}`;
        onChange(newValue);
      }}
      onAddAttachments={onAddAttachments}
      isEditMessage={isEditMessage}
      isDisabled={isSendingMessage}
      verticalAlign={isScheduleMessage ? 'end' : 'center'}
      hideVoiceRecording={hideVoiceRecording}
      hideAttachments={hideAttachments}
    />
  ), [
    onChange,
    text,
    onAddAttachments,
    isEditMessage,
    isSendingMessage,
    inputRef,
    isScheduleMessage,
    hideVoiceRecording,
    hideAttachments,
  ]);

  const renderInputContent = useCallback(() => {
    if (isVoiceMemoRecording && !isEditMessage && !hideVoiceRecording) {
      return (
        <AudioMemoControls
          onStopRecording={() => stopVoiceRecording()}
        />
      );
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const newText = replaceLastWordWithEmoji(event.target.value);

        onChange(newText);

        if (
          event.key === 'Enter'
          && !event.shiftKey
          && !isMobileView
          && !isDisabled
          && !isSendingMessage
          && submitOnEnter
        ) {
          onSubmit(newText);
        }
      }
    };

    return (
      <>
        <StyledInput
          value={text}
          disabled={isSendingMessage}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder || texts.placeholder}
          multiline
          endAdornment={useExtras && Adornment}
          inputRef={inputRef}
          $isEditMessage={isEditMessage}
          maxRows={5}
          onKeyDown={handleKeyDown}
          $isScheduleMessage={isScheduleMessage}
          minRows={isScheduleMessage ? 5 : 1}
          {...extraProps}
        />
        {(!isEditMessage || showSubmitInEditMode) && (
          <>
            <SubmitButton
              onClick={onSubmit}
              disabled={isDisabled || isSendingMessage}
              $isSchedulerEnabled={!!onClickSchedule}
            >
              <SendIcon />
            </SubmitButton>
            {/* TODO: Design schedule message modal for mobile */}
            {onClickSchedule && !isMobileView && !isEditMessage && (
              <ScheduleMessageButton
                onClick={onClickSchedule}
                disabled={isSendingMessage}
              >
                <StyledPopoverIcon />
              </ScheduleMessageButton>
            )}
          </>
        )}
      </>
    );
  }, [
    isVoiceMemoRecording,
    text,
    placeholder,
    Adornment,
    isEditMessage,
    onSubmit,
    onClickSchedule,
    isDisabled,
    isSendingMessage,
    stopVoiceRecording,
    onChange,
    extraProps,
    inputRef,
    isMobileView,
    isScheduleMessage,
    useExtras,
    submitOnEnter,
    hideVoiceRecording,
    showSubmitInEditMode,
  ]);

  return (
    <MessageInputWrapper
      $padding={framePadding}
    >
      {renderInputContent()}
    </MessageInputWrapper>
  );
};

MessageInput.propTypes = {
  text: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onAddAttachments: PropTypes.func,
  onClickSchedule: PropTypes.func,
  isEditMessage: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSendingMessage: PropTypes.bool,
  framePadding: PropTypes.number,
  inputRef: PropTypes.object,
  isMobileView: PropTypes.bool,
  isScheduleMessage: PropTypes.bool,
  useExtras: PropTypes.bool,
  submitOnEnter: PropTypes.bool,
  hideVoiceRecording: PropTypes.bool,
  hideAttachments: PropTypes.bool,
  showSubmitInEditMode: PropTypes.bool,
};

MessageInput.defaultProps = {
  placeholder: '',
  onAddAttachments: () => [],
  onSubmit: () => {},
  onClickSchedule: undefined,
  isEditMessage: false,
  isDisabled: false,
  isSendingMessage: false,
  framePadding: 10,
  inputRef: {},
  isMobileView: false,
  isScheduleMessage: false,
  useExtras: true,
  submitOnEnter: true,
  hideVoiceRecording: false,
  hideAttachments: false,
  showSubmitInEditMode: false,
};

export default MessageInput;
